import { Spin } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";

import { useContractProfileLoadQuery } from "src/app/features/ContractProfile/services/server/ContractProfileApi";
import type { ContractProfileEntity } from "src/app/features/ContractProfile/types/ContractProfileEntity";
import { FormManager } from "../../../../../components/Form/FormManager";
import { useLoadFormDefinitionQuery } from "../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH } from "../../../../../core/router/types/routerTypes";
import { showSuccessNotification, showWarningNotification } from "../../../../../core/setup/redux/state/notifications/notificationsState";
import { deepClone, isTheSameByDeepCompare } from "../../../../../core/utils/utilFunctions";
import { useContractPhysicalLoadQuery, useContractPhysicalUpdateMutation } from "../../services/server/ContractApi";
import type { ContractEntity } from "../../types/ContractEntity";
import type { ContractAppProfileEntity } from "../../types/subTypes/ContractAppProfileEntity";

export const ContractPhysicalEditForm: FC<FormProps> = ({ runSubmit }) => {
    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("contract-physical");

    const [formData, setFormData] = useState<ContractEntity>({} as ContractEntity);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [saveFormData, { isLoading: saveFormIsInProgress }] = useContractPhysicalUpdateMutation();
    const { id } = useParams();

    // HANDLE INITIAL VALUES
    const { data: loadFormData, isFetching: loadFormIsInProgress } = useContractPhysicalLoadQuery({ entityId: id });

    const [isNewContractProfileIdChanged, setIsNewContractProfileIdChanged] = useState<boolean>(false);

    const { data: contractProfileEntity } = useContractProfileLoadQuery(
        { entityId: isNewContractProfileIdChanged ? `${formData.contractProfileId}` : undefined },
        { skip: !isNewContractProfileIdChanged || !formData.contractProfileId },
    );

    useEffect(() => {
        if (loadFormData) {
            setFormData(loadFormData);
        }
    }, [loadFormData, setFormData]);

    // HANDLE ON CHANGE
    const onChangeCallback = useCallback(
        (newData: ContractEntity) => {
            if (newData.contractProfileId !== formData?.contractProfileId) {
                setIsNewContractProfileIdChanged(true);
            }

            // appProfileListIdCheckReadOnly is used to control visibility of field in form, must be passed to formData
            setFormData({
                ...newData,
                // if there is no CP, all fields are enabled
                appProfileListIdCheckReadOnly: newData.contractProfileId === undefined ? false : formData?.appProfileListIdCheckReadOnly,
                acquirerIdCheckReadOnly: true,
            });
        },
        [formData?.appProfileListIdCheckReadOnly, formData?.contractProfileId],
    );

    // HANDLE WATCH FIELDS
    const updateWatchedFields = useCallback(() => {
        if (!contractProfileEntity || !setIsNewContractProfileIdChanged) return null;

        const contractProfile = contractProfileEntity;
        const newFormData = deepClone(formData);

        // HANDLE UPDATE FIELDS
        // these are fields that should inherit values from the contract profile
        const updateFields = [
            "acquirerId",
            "appProfileListId",
            "receiptHeader",
            "merchantData",
            "receiptFooter",
            "messageForSuccessfulTransaction",
            "messageForFailedTransaction",
            "courtesyMessage",
        ];
        if (contractProfile) {
            updateFields?.forEach((fieldName: string) => {
                newFormData[fieldName] = contractProfile[fieldName as keyof ContractProfileEntity];
            });
        }

        // HANDLE VISIBILITY OF FIELDS FROM RO PROPERTY IN CONTRACT PROFILE
        // the RO field is added to the entity to allow to disable in Form Layout
        // the newFormData property name must be the contract (not contractProfile) field plus RO
        newFormData.appProfileListIdCheckReadOnly = contractProfile?.["applicationProfileListRO" as keyof ContractProfileEntity];

        return newFormData;
    }, [contractProfileEntity, formData, setIsNewContractProfileIdChanged]);

    // HANDLE CP ID CHANGED
    useEffect(() => {
        if (!isNewContractProfileIdChanged || !contractProfileEntity) {
            return;
        }

        let newFormData = updateWatchedFields();
        if (newFormData && !isTheSameByDeepCompare(formData, newFormData)) {
            setIsNewContractProfileIdChanged(false);
            setFormData(newFormData);
            newFormData = null;
        }
    }, [formData, updateWatchedFields, isNewContractProfileIdChanged, contractProfileEntity]);

    // HANDLE ON SAVE
    const shouldSaveContractAppProfile = ({ acceptMode, routingId, merchantId, hostOperatorId }: ContractAppProfileEntity): boolean => {
        return !!acceptMode || (!acceptMode && !routingId && !merchantId && !hostOperatorId);
    };

    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            // a acceptedCardProfiles (accepted card type) is only valid to send to the BE if it has acceptedMode defined, alternatively if it is empty it should be skipped
            if (formData.acceptedCardProfiles?.some((item) => !shouldSaveContractAppProfile(item))) {
                dispatch(
                    showWarningNotification({
                        text: i18n.t("contract.error.contractAppProfiles"),
                        autoCloseDisabled: true,
                    }),
                );
                return;
            }
            // Only include acceptedCardProfiles if it has acceptedMode defined
            const updatedFormData = {
                ...formData,
                acceptedCardProfiles: formData.acceptedCardProfiles?.filter((item) => !!item.acceptMode),
            };

            const result = await saveFormData({ data: updatedFormData });

            const hasError = "error" in result;
            if (!hasError) {
                dispatch(
                    showSuccessNotification({
                        text: i18n.t("common.formUpdateSuccess", {
                            id: result.data.id,
                            resource: "Contract",
                        }),
                        autoCloseDisabled: false,
                    }),
                );
                navigate(`${BASE_PATH}/${ResourceName.CONTRACTS}`);
            }
        },
        [saveFormData, formData, navigate, dispatch],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching || loadFormIsInProgress}>
            {formDefinition?.formConfiguration && formData && (
                <FormManager<ContractEntity>
                    name="PhysicalContractEditForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
